import React from "react";
import { SvgIcon } from "@/svg";
import { Link } from "react-router";
import { isAllBusiness } from "@/api";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { BusinessPicker } from "./BusinessPicker";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { BusinessAvatar } from "./BusinessAvatar";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import clsx from "clsx";
import { useAuth } from "@/Auth";
import { mergeUserEmails } from "@/utils/mergeUserEmails";
import { ThemeToggle } from "./ThemeToggle";
import { ENABLE_THEMING } from "@/constants";

export type QuickSettingsProps = {
  hideArrows?: boolean;
  hideBusinessName?: boolean;
  onBusinessChange?: (id: string) => Promise<void>;
};

export function QuickSettings({
  hideArrows = false,
  hideBusinessName = false,
  onBusinessChange,
}: QuickSettingsProps) {
  const auth = useAuth();
  const businesses = useGetBusinesses();
  const userEmails = React.useMemo(() => {
    if (!auth.isAuthenticated) {
      return "";
    }
    return mergeUserEmails(auth.user);
  }, [auth.isAuthenticated, auth.user]);

  const businessContext = useBusinessContext();

  if (businessContext.isLoading) {
    return null;
  }

  return (
    <Popover className="group w-auto! h-auto! flex-1 shrink min-w-0">
      {({ close }) => (
        <>
          <PopoverButton
            className={clsx(
              "h-full w-full flex space-x-3 justify-between",
              "flex items-center font-medium text-feintGray group-hover:text-dark group-data-open:text-dark",
            )}
          >
            <BusinessAvatar
              businessDisplayName={businessContext.business.display_name}
              className={isAllBusiness(businessContext.business.id) ? "bg-cyan" : "bg-primaryColor"}
            />
            {!hideBusinessName && (
              <div
                className="text-sm text-left truncate flex-1"
                title={businessContext.business.display_name}
              >
                {businessContext.business.display_name}
              </div>
            )}
            {!hideArrows && (
              <div>
                <SvgIcon icon="UpDownArrows" />
              </div>
            )}
          </PopoverButton>
          <PopoverPanel
            anchor="bottom start"
            transition
            className={clsx(
              "bg-pureWhite font-medium text-feintGray rounded-2xl shadow-xl w-96 flex flex-col",
              "transition origin-top duration-200 data-closed:scale-95 data-closed:opacity-0",
              "[--anchor-gap:0.3rem] [--anchor-max-height:70vh]",
            )}
          >
            <div className="flex items-center justify-between p-4">
              <div className="text-sm truncate max-w-[170px]" title={userEmails}>
                {userEmails}
              </div>
              <div className="flex items-center text-sm gap-4">
                <Link to="/settings" className="cursor-pointer hover:text-primaryColor">
                  Settings
                </Link>
              </div>
            </div>
            {(businesses.data?.length ?? 0) > 1 && (
              <BusinessPicker
                businesses={businesses}
                onClose={close}
                onBusinessChange={onBusinessChange}
              />
            )}
            {ENABLE_THEMING && (
              <div className="border-t border-backgroundGray p-4">
                <ThemeToggle />
              </div>
            )}
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
}
